import React, { useState, useEffect, useMemo, FC } from "react"
import Styles from "../styles/PageLinkText.module.scss"
import UserAvatar from "./UserAvatar"
import { Link } from "gatsby"
import moment from "moment"
import { UserType } from "../types/models/User";
import { Overwrite } from "../types/utils";
import ActivityIndicator from "./atoms/ActivityIndicator";
import { FileType } from "../types/models/Post";
import { useIsLoggedIn } from "../hooks/useIsLoggedIn";
import client from "../apis/client";


// const Accepted_200 = "Accespted_200"
// const Unauthorized_401 = "Unauthorized_401"
// const Forbidden_403 = "Forbidden_403"
// const NotFound_404 = "NotFound_404"
// const OtherErrors = "OtherErrors"

export type linkPageObjectType = {
    pk: string;
    text: string;
    file_type: number;
    file?: string;
    thumbnail?: string;
    author: UserType;
    created_at: string;
}

export type ImageLinkPageObjectType = Overwrite<linkPageObjectType, {
    file: string
    thumbnail: undefined
}>

export type VideoLinkPageObjectType = Overwrite<linkPageObjectType, {
    file: string
    thumbnail: string
}>

export const isVideoLinkObject = ( link: any ): link is VideoLinkPageObjectType => link.file_type === FileType.VIDEO
export const isImageLinkObject = ( link: any ): link is ImageLinkPageObjectType => link.file_type === FileType.IMAGE

type Props = {
    children: JSX.Element,
    postID: string
}

export const PageLinkText: FC<Props> = ( {
    children,
    postID
} ) => {

    const [ linkPageObject, setlinkPageObject ] = useState<linkPageObjectType | null>( null );
    const [ errorMessage, setErrorMessage ] = useState( "" );
    const isLoggedIn = useIsLoggedIn()


    useEffect( () => {
        getlinkPostData()
    }, [] )

    const getlinkPostData = async () => {
        const headers = isLoggedIn ?
            {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            } :
            {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            }
        await client.get( `/api/post/${ postID }/summary/`, headers )
            .then( async response =>
                setlinkPageObject( response.data.post )
            )
            .catch( error => {
                // console.log( "ERROR", error.response )
                if ( !error.response ) {
                    setErrorMessage( "なんらかのエラーでリンク先の投稿を取得できませんでした" )
                }
                if ( error.response.status === 404 ) {
                    setErrorMessage( "存在しない投稿です" )
                    return
                }
                if ( error.response.status === 403 ) {
                    setErrorMessage( "この投稿を閲覧する権限がありません" )
                    return
                }
                if ( error.response.status === 401 ) {
                    setErrorMessage( "この投稿を閲覧する権限がありません。" )
                    return
                }

            } );
    }

    const renderLoader = () =>
        <Link
            to={ `/post/${ postID }` }
            state={ { postID } }
            className={ Styles.container }>
            <div className={ Styles.link_container }>
                <ActivityIndicator style={ { transform: "scale(0.5)" } } />
            </div>
        </Link>

    const renderErrorMessage = () =>
        <div className={ Styles.link_container }>
            <span>{ errorMessage }</span>
        </div>

    const renderMedia = ( linkPageObject: VideoLinkPageObjectType | ImageLinkPageObjectType ) =>
        <div className={ Styles.image_wrapper }>
            <img
                className={ Styles.image }
                src={
                    isVideoLinkObject( linkPageObject ) ?
                        linkPageObject.thumbnail :
                        linkPageObject.file } />
        </div>

    const renderLinkPage = () =>
        linkPageObject &&
        <Link
            to={ `/post/${ postID }` }
            state={ {
                postID
            } }
            className={ Styles.container }>
            <div className={ Styles.link_container }>
                <div className={ Styles.info_container }>
                    <div className={ Styles.author_wrapper }>
                        { linkPageObject.author ?
                            <UserAvatar
                                userName={ linkPageObject.author.name }
                                userID={ linkPageObject.author.pk }
                                profileImageURL={ linkPageObject.author.profile_image_100 }
                                size={ 32 }
                                avatarSize={ 16 }
                                style={ { height: 32, width: 32 } } /> :
                            <UserAvatar
                                size={ 32 }
                                avatarSize={ 16 }
                                style={ { height: 32, width: 32 } } /> }
                        { <span className={ Styles.author_name }>
                            { linkPageObject.author ?
                                linkPageObject.author.name :
                                "匿名ユーザー" }
                        </span> }
                    </div>
                    <span className={ Styles.date }>{ `${ moment( linkPageObject.created_at ).format( 'YYYY年MM月DD日' ) }` }</span>
                </div>
                <div className={ Styles.content }>
                    { linkPageObject.text &&
                        <p className={ Styles.text }>{ linkPageObject.text }</p> }
                    { ( isVideoLinkObject( linkPageObject ) || isImageLinkObject( linkPageObject ) ) &&
                        renderMedia( linkPageObject ) }
                </div>
            </div>
        </Link>

    return useMemo( () =>
        <div style={ { display: "flex", flexDirection: "column" } }>
            { children }
            { linkPageObject ?
                renderLinkPage() :
                renderLoader() }
            { errorMessage && renderErrorMessage() }
        </div>, [ postID, linkPageObject ] )
}
